import { useState } from 'react'
import { useDispatch } from 'react-redux'
import styled, { css } from 'styled-components'

import { AddressCard, Button, DrawerBodyWrapper, DrawerFooterWrapper, H5, TOAST_TYPE } from '@base'
import { Select } from '@baseV2'
import { DRAWER_TYPES, MBOOK_ORGANISATIONS, strings } from '@constants'
import { addAddressToOrganisation, getUserAddressesApi, getWarehouseAddress } from '@data'
import { Address } from '@data/model'
import { showLoader, showToast } from '@data/state/action'
import { toggleDrawer } from '@data/state/action/root'
import { Devices, Spacings } from '@styles'

const BodyWrapper = styled.div`
	padding-top: ${Spacings.SPACING_1B};
`

const LabelHeader = styled(H5)`
	color: ${({ theme }) => theme.palette.text.black};
	margin: ${Spacings.SPACING_4B} 0;
`

const AddressListWrapper = styled.div`
	display: flex;
	flex-direction: column;

	@media ${Devices.tablet} {
		flex-direction: row;
		flex-wrap: wrap;
	}
`

const AddressCardWrapper = styled.div`
	margin-bottom: ${Spacings.SPACING_2B};
	flex-grow: 1;
	flex-basis: 100%;

	border: 1px solid transparent;
	border-radius: ${Spacings.SPACING_2B};
	overflow: hidden;
	transition: border-color 0.15s ease-in-out;

	${({ selected }) => {
		if (selected) {
			return css`
				cursor: initial;
				border-color: ${({ theme }) => theme.palette.background.blue};
			`
		}
	}}

	${({ hoverable = true }) => {
		if (hoverable) {
			return css`
				cursor: pointer;
				&:hover {
					border-color: ${({ theme }) => theme.palette.background.blue};
				}
			`
		}
	}}
`

const AddressListDiv = styled.div`
	margin-top: ${Spacings.SPACING_6B};
`

/*
Consignee (MBOOK_ORGANISATIONS + selectedUser) -> getWarehouseAddress -> State (warehouse) select -> addresses list.
If selectedUser is the consignee, warehouses are skipped and the user's addresses are fetched directly.
Does not have any footer buttons -> callback added on selecting address after selecting state
*/
const ShippingDetailsSpo = ({ state, consigneeList, valueChangeHandler }) => {
	const [consignee, setConsignee] = useState(null)
	const [warehouses, setWarehouses] = useState([])
	const [warehouse, setWarehouse] = useState(null)
	const [userAddresses, setUserAddresses] = useState([])
	const [selectedAddress, setSelectedAddress] = useState({})
	const globalDispatch = useDispatch()
	const selectedUser = state?.selectedUser
	const consignees = selectedUser ? [...MBOOK_ORGANISATIONS, selectedUser] : MBOOK_ORGANISATIONS
	const isUserConsignee = !!consignee?.user_id
	const addresses = isUserConsignee ? userAddresses : warehouse?.addresses

	const updateCallback = () => {}
	const addToOrgCallback = (address) => {
		globalDispatch(showLoader(true))
		addAddressToOrganisation(address, consignee?.organisation_id)
			.then((res) => {
				globalDispatch(showToast(true, 'Successfully added a new address', { type: TOAST_TYPE.SUCCESS }))
				selectAddress(new Address(res))()
			})
			.catch((err) => {
				let error_msg = 'Error while adding a new address'
				if (err?.response?.data?.client_error_code === 'E_002') {
					error_msg = err?.response?.data?.message
				}
				globalDispatch(showToast(true, error_msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => {
				globalDispatch(showLoader(false))
			})
	}

	const addAddressHandler = () => {
		globalDispatch(
			toggleDrawer(true, DRAWER_TYPES.ADD_NEW_ADDRESS, {
				addToNewAuction: true,
				updateCallback,
				label: strings('add_new_address'),
				addToNewEnquiry: true,
				addToOrgCallback,
				addToOrg: true,
			})
		)
	}

	const setConsigneeHandler = (value) => {
		setConsignee(value)
		setWarehouse(null)
		setWarehouses([])
		setUserAddresses([])
		valueChangeHandler('consignee')(value)
		globalDispatch(showLoader(true))
		const request = value?.user_id
			? getUserAddressesApi(value.user_id).then((response) => setUserAddresses(response ?? []))
			: getWarehouseAddress(value?.organisation_id).then((response) => setWarehouses(response ?? []))
		request
			.catch((error) => {
				const msg =
					error?.response?.data?.message ?? (value?.user_id ? strings('msg_could_not_fetch_user_addresses') : 'Could not fetch warehouses')
				globalDispatch(showToast(true, msg, { type: TOAST_TYPE.ERROR }))
			})
			.finally(() => globalDispatch(showLoader(false)))
	}

	const selectAddress = (val) => () => {
		valueChangeHandler('shipping_address')(val)
		setSelectedAddress(val)
		globalDispatch(toggleDrawer(false))
	}

	return (
		<>
			<DrawerBodyWrapper>
				<BodyWrapper>
					<Select
						label='Consignee'
						placeholder='Select consignee'
						data={consignees}
						value={consignee}
						displayKey='organisation_name'
						primaryKey='organisation_id'
						onChange={setConsigneeHandler}
					/>

					{consignee && !isUserConsignee && (
						<AddressListDiv>
							<Select
								label='State'
								placeholder='Select state'
								data={warehouses}
								value={warehouse}
								displayKey='warehouse_name'
								primaryKey='external_id'
								onChange={setWarehouse}
							/>
						</AddressListDiv>
					)}

					{(isUserConsignee || warehouse) && (
						<AddressListDiv>
							<div>
								<Button small uppercase type='primary' onClick={addAddressHandler}>
									{strings('add_new_address')}
								</Button>
							</div>
							<LabelHeader>{strings('select_from_existing_addresses')}</LabelHeader>
							<AddressListWrapper>
								{(addresses ?? []).map((address, index) => {
									return (
										<AddressCardWrapper
											selected={address?.address_id === selectedAddress?.address_id}
											onClick={selectAddress(address)}
											key={`address-card-from-addressbook-${index}`}
										>
											<AddressCard data={address} />
										</AddressCardWrapper>
									)
								})}
							</AddressListWrapper>
						</AddressListDiv>
					)}
				</BodyWrapper>
			</DrawerBodyWrapper>
			<DrawerFooterWrapper></DrawerFooterWrapper>
		</>
	)
}

export default ShippingDetailsSpo
